/**
 * useUserProfileImage Hook
 * Handles image upload and cropping logic for user profile pictures
 */

import { useState, useRef, useCallback, createRef } from "react";

export const useUserProfileImage = () => {
  const [image, setImage] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState("");
  const [removeProfileImage, setRemoveProfileImage] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [profileImageName, setProfileImageName] = useState(null);
  const [fileSelectIsLoading, setFileSelectIsLoading] = useState(false);
  
  const fileInputRef = useRef(null);
  const cropperRef = createRef();
  
  // Handle file input change
  const handleFileInputChange = useCallback((event, onImageReady) => {
    setFileSelectIsLoading(false);
    const file = event.target.files[0];
    setProfileImageName(file);
    
    if (file) {
      const fileReader = new FileReader();
      fileReader.onload = () => {
        setImage(fileReader.result);
        if (onImageReady) {
          onImageReady();
        }
      };
      fileReader.readAsDataURL(file);
    }
  }, []);
  
  // Handle file input cancel
  const handleFileInputCancel = useCallback(() => {
    setFileSelectIsLoading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);
  
  // Handle profile picture change
  const handleProfilePicChange = useCallback(() => {
    if (fileInputRef.current && !fileSelectIsLoading) {
      setFileSelectIsLoading(true);
      fileInputRef.current.click();
    }
  }, [fileSelectIsLoading]);
  
  // Handle crop click
  const handleCropClick = useCallback((onCropComplete) => {
    if (typeof cropperRef.current?.cropper !== "undefined") {
      const croppedImage = cropperRef.current?.cropper.getCroppedCanvas().toDataURL();
      setUploadedImageUrl(croppedImage);
      setProfileImage(croppedImage);
      
      if (onCropComplete) {
        onCropComplete();
      }
    }
  }, [cropperRef]);
  
  // Resize image
  const resizeImage = useCallback(async (base64Image, maxWidth) => {
    return new Promise((resolve) => {
      const image = new Image();
      image.src = base64Image;

      image.onload = () => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        const aspectRatio = image.width / image.height;
        const newHeight = maxWidth / aspectRatio;

        canvas.width = maxWidth;
        canvas.height = newHeight;

        context.drawImage(image, 0, 0, maxWidth, newHeight);

        const resizedImage = canvas.toDataURL('image/jpeg');
        resolve(resizedImage);
      };
    });
  }, []);
  
  // Reset image state
  const resetImageState = useCallback(() => {
    setImage(null);
    setUploadedImageUrl("");
    setRemoveProfileImage(false);
    setProfileImage(null);
    setProfileImageName(null);
    setFileSelectIsLoading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);
  
  return {
    // State
    image,
    uploadedImageUrl,
    removeProfileImage,
    profileImage,
    profileImageName,
    fileSelectIsLoading,
    
    // Refs
    fileInputRef,
    cropperRef,
    
    // Handlers
    handleFileInputChange,
    handleFileInputCancel,
    handleProfilePicChange,
    handleCropClick,
    resizeImage,
    resetImageState,
    
    // Setters
    setImage,
    setUploadedImageUrl,
    setRemoveProfileImage,
    setProfileImage,
    setProfileImageName,
    setFileSelectIsLoading,
  };
};
